/**
 * Painel de detalhe do item, com a solicitação de acesso embutida.
 *
 * O esboço usa dois diálogos empilhados — detalhe, e depois "solicitar acesso".
 * Aqui é um só, que troca de modo. Diálogo sobre diálogo é armadilha de foco:
 * fechar o de cima com Esc costuma fechar os dois, e o leitor de tela se perde
 * entre duas regiões `aria-modal`.
 *
 * ## Acessibilidade
 *
 * Esc fecha, clique no fundo fecha, o foco entra ao abrir e **volta para onde
 * estava** ao fechar. Sem essa devolução, quem navega por teclado é jogado para
 * o começo da página a cada card que abre.
 */
import { useEffect, useRef, useState } from 'react';
import { logoUrl, apiFetch, SessionError, goToSignIn } from '../api.js';
import { notify } from './Toast.jsx';
import { emitPendingWatch } from '../accessEvents.js';
import { DestinationIcon, destinationsOf } from './DestinationIcon.jsx';

export function ItemPanel({ item, onClose, onSubmitted, isAdmin = false }) {
  const [mode, setMode] = useState('detalhe');
  const [justification, setJustification] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const boxRef = useRef(null);
  const previousFocus = useRef(null);

  useEffect(() => {
    previousFocus.current = document.activeElement;
    boxRef.current?.focus();
    document.body.classList.add('sem-rolagem');

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('sem-rolagem');
      // Devolve o foco ao card que abriu o painel.
      previousFocus.current?.focus?.();
    };
  }, [onClose]);

  const { access } = item;
  const destinations = destinationsOf(item);

  async function submit() {
    setSending(true);
    setError(null);
    try {
      const r = await apiFetch(`/api/items/${encodeURIComponent(item.id)}/request`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ justification: justification.trim() || undefined }),
      });
      notify(r.message, r.alreadyExisted ? 'atencao' : 'sucesso');
      // Acelera o sino: é a janela em que a pessoa está de fato esperando.
      emitPendingWatch();
      onClose();
      onSubmitted?.();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  const rows = [
    ['Área responsável', item.category],
    ['Responsável', item.ownerName],
    ['Acesso', item.accessLabel],
    ['Fornecedor', item.vendor],
    ['Plataforma', item.platformType],
  ].filter(([, v]) => v);

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="item-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="item-panel-title"
        tabIndex={-1}
        ref={boxRef}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header">
          <span className="panel-avatar" aria-hidden="true">
            <span className="mark-initials">
              {item.initials ?? item.name.slice(0, 2).toUpperCase()}
            </span>
            {item.hasLogo && (
              <img
                className="mark-logo"
                src={logoUrl(item.id, item.logoUpdatedAt)}
                alt=""
                loading="lazy"
              />
            )}
          </span>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <div className="panel-body">
          <div>
            <h2 id="item-panel-title" className="panel-title">
              {item.name}
            </h2>
            {item.description && <p className="panel-desc">{item.description}</p>}

            {/* Quantas conversas este agente já teve.
                `null` é diferente de zero: zero é "ninguém usou ainda", `null`
                é "a plataforma não tem telemetria deste item" — e dizer zero
                nesse caso seria mentir sobre um agente que pode estar em uso
                pesado fora daqui. Por isso a linha some quando é `null`. */}
            {typeof item.conversationCount === 'number' && (
              <p className="conversation-count">
                {isAdmin ? (
                  <a href={`/admin?aba=conversations&item=${encodeURIComponent(item.id)}`}>
                    {item.conversationCount === 1
                      ? '1 conversa'
                      : `${item.conversationCount.toLocaleString('pt-BR')} conversas`}
                  </a>
                ) : (
                  <span>
                    {item.conversationCount === 1
                      ? '1 conversa'
                      : `${item.conversationCount.toLocaleString('pt-BR')} conversas`}
                  </span>
                )}
              </p>
            )}
          </div>

          {mode === 'detalhe' ? (
            <>
              <dl className="facts">
                {rows.map(([label, value]) => (
                  <div className="fact-row" key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>

              {access.notice && <p className="access-note">{access.notice}</p>}

              {/* ## Por que a condição não é mais só `!canOpen`
                  Desde 25/10 um item pode ABRIR e mesmo assim não aceitar
                  solicitação — é o caso da ferramenta de uso fechado. Com a
                  condição antiga, o botão sumia e nada explicava o porquê, que
                  é exatamente o estado que o motivo existe para evitar.
                  A regra passou a ser: mostre a razão sempre que faltar uma
                  das duas portas, abrir ou pedir. */}
              {access.reason && (!access.canOpen || !access.canRequest) && (
                <p className="access-note">{access.reason}</p>
              )}

              {item.tags?.length > 0 && (
                <div>
                  <div className="section-label">Tags</div>
                  <div className="tag-row">
                    {item.tags.map((g) => (
                      <span className="tag" key={g}>
                        {g}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="request-form">
              <p className="panel-desc">
                Ao confirmar, abriremos um chamado no ServiceNow. Você será avisado quando ele for
                atendido.
              </p>

              <label className="field-label" htmlFor="justification">
                Justificativa <span className="optional">(opcional)</span>
              </label>
              <textarea
                id="justification"
                className="textarea"
                rows={3}
                maxLength={2000}
                placeholder="Descreva brevemente por que precisa deste item…"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
              />

              {error && (
                <p className="error-note" role="alert">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="panel-actions">
          {mode === 'detalhe' ? (
            <>
              {/* Um botão por destino: o item pode abrir no Teams e no
                  Microsoft 365, e são caminhos diferentes para a mesma coisa.
                  A lista sai de `destinationsOf`, a mesma que o card usa — duas
                  montagens divergiriam na primeira regra nova.

                  `noopener` obrigatório: sem ele a página de destino recebe
                  `window.opener` e pode reescrever a nossa aba. */}
              {destinations.map((d) => (
                <a
                  key={d.url}
                  className={`button ${d.primary ? 'primary' : 'secondary'} with-icon`}
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Abrir no ${d.where}`}
                >
                  <DestinationIcon url={d.url} />
                  <span>{d.label}</span>
                </a>
              ))}
              {access.canRequest && (
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => setMode('solicitar')}
                >
                  Solicitar acesso
                </button>
              )}
              {!access.canOpen && !access.canRequest && (
                <button className="button ghost" type="button" onClick={onClose}>
                  Fechar
                </button>
              )}
            </>
          ) : (
            <>
              <button
                className="button ghost"
                type="button"
                onClick={() => setMode('detalhe')}
                disabled={sending}
              >
                Voltar
              </button>
              <button className="button primary" type="button" onClick={submit} disabled={sending}>
                {sending ? 'Enviando…' : 'Enviar solicitação'}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
